import React from 'react'
import {
  TabNav,
  TabPanel,
  PanelContainer,
  ButtonContainer,
  TabButton,
} from '~/components/shared/TabNav'
import BasicsSection from './BasicsSection'
import ServicesSection from './ServicesSection'
import CoachNotesSection from './CoachNotesSection'
import MessagesSection from './MessagesSection'
import CoachingSummarySection from './CoachingSummarySection'
import SettingsAndDocumentsSection from './SettingsAndDocumentsSection'
import CandidateActivitySection from './CandidateActivitySection'
import { CandidateProvider, useCandidate } from './CandidateProvider'
import ResumeReviewDetail from './ResumeReviewDetail'
import { cn } from '~/lib/utils'
import CandidateStatusDialog from './CandidateStatusDialog'
import OrientationCallDetail from './OrientationCallDetail'

const CoachCandidateViewInner = () => {
  const {
    detailType,
    candidate,
    candidateStatus,
    toggleShowCandidateStatusDialog,
  } = useCandidate()
  const { impersonationPath, fullName } = candidate || {}

  const coachUtilizationColorMap = {
    Good: 'text-green-500',
    'Above Average': 'text-yellow-500',
    High: 'text-red-500',
  }
  const candidateStatusColorMap = {
    Paused: 'text-red-500',
    'Active Search': 'text-green-500',
    Landed: 'text-blue-500',
    Expired: 'text-gray-500',
  }

  return (
    <article className="font-roboto">
      <header className="flex flex-row justify-between border-b-2 border-solid border-silver pb-6 pt-8 font-roboto">
        <div className="flex flex-col justify-between gap-2">
          <h1 className="font-roboto-slab font-normal text-cloud-burst">
            {fullName}
          </h1>
          {impersonationPath && (
            <a
              href={impersonationPath}
              className="!underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Impersonate Candidate
            </a>
          )}
          {detailType ? (
            <a href="#" className="!underline">
              Back to all coaching services
            </a>
          ) : (
            <a className="!underline" href="/coach/dashboard">
              Back to Dashboard
            </a>
          )}
        </div>
        <div className="flex flex-col items-end justify-end gap-5">
          {(candidateStatus == 'Paused' ||
            candidateStatus == 'Active Search') && (
            <div
              className={cn(
                'flex cursor-pointer items-center gap-2',
                candidateStatusColorMap[candidateStatus],
              )}
              onClick={() => toggleShowCandidateStatusDialog()}
            >
              <CandidateStatusDialog />
              {candidateStatus}
              <i
                className={cn(
                  '',
                  candidateStatus == 'Paused'
                    ? 'el-icon-pause-alt'
                    : 'el-icon-search-alt',
                )}
              ></i>
            </div>
          )}
          {candidate?.candidateStatus == 'Landed' ||
            (candidate?.candidateStatus == 'Expired' && (
              <div className="text-green-500">{candidateStatus}</div>
            ))}
          {candidate?.coachUtilization && (
            <div>
              Coach Utilization:{' '}
              <span
                className={coachUtilizationColorMap[candidate.coachUtilization]}
              >
                {candidate.coachUtilization}
              </span>
            </div>
          )}
        </div>
      </header>
      <section className="mt-10">
        <BasicsSection />
      </section>
      <section className="my-10">
        <SwappablePanel />
      </section>
    </article>
  )
}

const SwappablePanel = () => {
  const { detailType } = useCandidate()

  switch (detailType) {
    case 'ResumeReview':
      return <ResumeReviewDetail />
    case 'OrientationCall':
      return <OrientationCallDetail />
    default:
      return <TabGroup />
  }
}

const TabGroup = () => {
  const { defaultActiveTab } = useCandidate()
  return (
    <TabNav defaultActiveTab={defaultActiveTab}>
      <ButtonContainer className="justify-start gap-4">
        <TabButton name="coach_services" text="Coach Services" />
        <TabButton name="coach_notes" text="Coach Notes" />
        <TabButton name="messages" text="Messages" />
        <TabButton name="coaching_summary" text="Coaching Summary" />
        <TabButton name="settings_and_documents" text="Settings & Documents" />
        <TabButton name="candidate_activity" text="Candidate Activity" />
      </ButtonContainer>
      <PanelContainer className="mt-6">
        <TabPanel name="coach_services">
          <ServicesSection />
        </TabPanel>
        <TabPanel name="coach_notes">
          <CoachNotesSection />
        </TabPanel>
        <TabPanel name="messages">
          <MessagesSection />
        </TabPanel>
        <TabPanel name="coaching_summary">
          <CoachingSummarySection />
        </TabPanel>
        <TabPanel name="settings_and_documents">
          <SettingsAndDocumentsSection />
        </TabPanel>
        <TabPanel name="candidate_activity">
          <CandidateActivitySection />
        </TabPanel>
      </PanelContainer>
    </TabNav>
  )
}

const CoachCandidateView = ({ ...props }) => {
  return (
    <CandidateProvider {...props}>
      <CoachCandidateViewInner />
    </CandidateProvider>
  )
}

export default CoachCandidateView
